import axios from "axios"
import API_ENDPOINTS from "../config/api"

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:3000/api"

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000, // 10 seconds timeout
})

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("access_token")
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

let isRefreshing = false
let failedQueue = []

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error)
    } else {
      prom.resolve(token)
    }
  })

  failedQueue = []
}

const refreshToken = async () => {
  try {
    const refreshToken = localStorage.getItem("refresh_token")
    const response = await api.post(API_ENDPOINTS.AUTH.REFRESH_TOKEN, {
      refresh_token: refreshToken,
    })
    const { access_token, refresh_token } = response.data
    localStorage.setItem("access_token", access_token)
    localStorage.setItem("refresh_token", refresh_token)
    return access_token
  } catch (error) {
    console.error("Error refreshing token:", error)
    throw error
  }
}

const MAX_RETRIES = 3
const RETRY_DELAY = 5000 // 5 seconds

api.interceptors.response.use(
  (response) => {

    // Check for empty data in successful responses
    if (response.status === 200 && (!response.data || (Array.isArray(response.data) && response.data.length === 0))) {
      console.warn("Received empty data from:", response.config.url)
      return Promise.reject(new Error("Empty data received"))
    }

    return response
  },
  async (error) => {
    const originalRequest = error.config

    console.error("API Error:", error.response?.status, error.config.url, error.message)

    // Special handling for login endpoint
    if (originalRequest.url === API_ENDPOINTS.AUTH.LOGIN) {
      const customError = new Error("Invalid email or password")
      customError.response = error.response
      return Promise.reject(customError)
    }

    if (error.message === "Empty data received" && !originalRequest._retryCount) {
      originalRequest._retryCount = 0

      const retryRequest = () => {
        originalRequest._retryCount++
        return new Promise((resolve) => {
          setTimeout(() => resolve(api(originalRequest)), RETRY_DELAY)
        })
      }

      while (originalRequest._retryCount < MAX_RETRIES) {
        try {
          return await retryRequest()
        } catch (retryError) {
          if (retryError.message !== "Empty data received" || originalRequest._retryCount >= MAX_RETRIES) {
            return Promise.reject(retryError)
          }
        }
      }
    }

    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject })
        })
          .then((token) => {
            originalRequest.headers["Authorization"] = "Bearer " + token
            return api(originalRequest)
          })
          .catch((err) => Promise.reject(err))
      }

      originalRequest._retry = true
      isRefreshing = true

      try {
        const newToken = await refreshToken()
        processQueue(null, newToken)
        api.defaults.headers.common["Authorization"] = "Bearer " + newToken
        originalRequest.headers["Authorization"] = "Bearer " + newToken
        return api(originalRequest)
      } catch (refreshError) {
        processQueue(refreshError, null)
        // If refresh token fails, redirect to login
        localStorage.removeItem("access_token")
        localStorage.removeItem("refresh_token")
        window.location.href = "/login"
        return Promise.reject(refreshError)
      } finally {
        isRefreshing = false
      }
    }

    return Promise.reject(error)
  },
)

export default api
